/* eslint-disable jsx-a11y/anchor-is-valid */
import { Dropdown } from 'react-bootstrap';
import { IoMdRefresh } from 'react-icons/io';
import { IoChevronBack, IoChevronForward, IoSearch, IoCloudDownloadOutline, IoAdd, IoClose } from 'react-icons/io5';
import { customerService } from '../../services/customer.service';
import LogicContext from '../../Context/LogicContext';
import { useEffect } from 'react';
import { useState } from 'react';
import { useContext } from 'react';
import CustomerContext from '../../Context/CustomerContext';
import Tooltip from '@mui/material/Tooltip';
import { SetCustomerEditAtom, GetCustomerEditAtom, SetCustomerList2, GetCustomerList2 } from '../../store/customerAtom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import moment from 'moment';

import GeneralCustomTable from '../../helpers/GeneralCustomTable';
import DelgroProgressBar from '../../generals/DelgroProgressBar';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import { formatCustomDate } from '../../helpers/formatCustomDate';
import CustomerPageFilter from './CustomerPageFilter';
import Papa from 'papaparse'; // ← Added for reliable CSV export
import StatusBadge from '../../generals/StatusBadge';

const columns = [
  {
    title: 'ID',
    key: 'Customer_Id',
    dataIndex: 'Customer_Id',
    className: 'text-primary',
    sorter: (a, b) => (a?.Customer_Id ?? '') - (b?.Customer_Id ?? ''),
  },
  {
    title: 'Name',
    dataIndex: 'Name',
    className: 'text-primary font-bold',
    sorter: (a, b) => (a?.Name ?? '').localeCompare(b?.Name ?? ''),
  },
  {
    title: 'Email',
    dataIndex: 'Email_Address',
    sorter: (a, b) => (a?.Email_Address ?? '').localeCompare(b?.Email_Address ?? ''),
  },
  {
    title: 'Mobile',
    dataIndex: 'Mobile',
    sorter: (a, b) => (a?.Mobile ?? '').localeCompare(b?.Mobile ?? ''),
  },
  {
    title: 'Type',
    dataIndex: 'Customer_Type_Name',
    sorter: (a, b) => (a?.Customer_Type_Name ?? '').localeCompare(b?.Customer_Type_Name ?? ''),
  },
  {
    title: 'Status',
    dataIndex: 'Customer_Status',
    sorter: (a, b) => a.Customer_Status.localeCompare(b.Customer_Status),
    render: (record) => (
      <StatusBadge statusId={record} statusName={record === 'A' ? 'Active' : 'InActive'} />
    ),
  },
  {
    title: "Referral Code",
    dataIndex: "Customer_Referral_Code",
    sorter: (a, b) => (a?.Customer_Referral_Code ?? '').localeCompare(b?.Customer_Referral_Code ?? '')
  },
  {
    title: "Referred By",
    dataIndex: "Referred_By_Code",
    sorter: (a, b) => (a?.Referred_By_Code ?? '').localeCompare(b?.Referred_By_Code ?? '')
  },
  {
    title: "Created Date",
    dataIndex: "Encoded_Date",
    sorter: (a, b) => (a?.Encoded_Date ?? '').localeCompare(b?.Encoded_Date ?? ''),
    render: record => <span> {formatCustomDate(record)} </span>,
  },
];

function CustomersPage() {
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: { option: 17 } });

  const logicContext = useContext(LogicContext);
  const customerContext = useContext(CustomerContext);
  const [customerSelect, setCustomerSelect] = useState([]);
  const [filteredCustomerList, setFilteredCustomersList] = useState([]);
  const navigate = useNavigate();
  const setCustomerAtomList = SetCustomerList2();
  const customerAtomList = GetCustomerList2();
  const [searchText, setSearchText] = useState('');

  const { pathname } = useLocation();
  var url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];
  const setCustomerEdit = SetCustomerEditAtom();

  // Load customer API
  const loadCustomerListAction = async () => {
    customerContext.getCustomerTableHeader();
    const response = await customerService.getCustomerListNoCache();
    if (response.data?.customer_info?.length) {
      setCustomerAtomList(response?.data);
      setFilteredCustomersList(response?.data?.customer_info);
    }
  };

  useEffect(() => {
    document.title = 'Customer page';
    setCustomerEdit({});
    if (customerAtomList.customer_info?.length) {
      setFilteredCustomersList(customerAtomList.customer_info);
    } else {
      loadCustomerListAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAtomList.customer_info?.length]);

  function selectCustomer(selectionList) {
    setCustomerSelect(selectionList);
  }

  const handleRowClick = record => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/customer-details/${record?.Customer_Id}`);
  };

  const searchByDate = async () => {
    const formData = getValues();
    const response = await customerService.getCustomerByDate(formData);
    if (response?.data?.length) {
      setFilteredCustomersList(response?.data);
    } else {
      logicContext.showToast('No result found', '');
      setFilteredCustomersList([]);
    }
  };

  const onClearInput = () => {
    setSearchText('');
    setFilteredCustomersList(customerAtomList.customer_info || []);
  };

  const handleSearch = text => {
    setSearchText(text);
    if (text.trim() === '') {
      setFilteredCustomersList(customerAtomList.customer_info || []);
      return;
    }

    const searchTerm = text.toLowerCase();
    const filtered = (customerAtomList.customer_info || []).filter(credit =>
      Object.values(credit).some(value => value?.toString().toLowerCase().includes(searchTerm))
    );
    setFilteredCustomersList(filtered);
  };

  const onFilterSelect = sel => {
    const option = sel.target.value;
    setValue('option', option || 17);
  };

  // ====================== EXPORT CSV ======================
  const exportToCSV = () => {
    if (!filteredCustomerList?.length) {
      logicContext.showToast('No data to export', 'warning');
      return;
    }

    const headers = columns.map(col => col.title);
    const keys = columns.map(col => col.dataIndex);

    const dataForExport = filteredCustomerList.map(row => {
      const obj = {};
      keys.forEach((key, i) => {
        let value = row[key] ?? '';

        // Apply same formatting as the table
        if (key === 'Encoded_Date') {
          value = formatCustomDate(value);
        }

        obj[headers[i]] = value;
      });
      return obj;
    });

    const csv = Papa.unparse(dataForExport, { header: true });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `customers_${moment().format('YYYY-MM-DD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    logicContext.showToast('✅ CSV exported successfully!', 'success');
  };
  // =======================================================

  return (
    <section className="@container">
      <section className="w-full">
        {/* Quick Stats Cards */}
        <div className="grid gap-4 grid-auto-fit-[150px]">
          <div
            onClick={() => {
              const today = moment().format('YYYY-MM-DD');
              setValue('fr_date', today);
              setValue('to_date', today);
              searchByDate();
            }}
            className="cursor-pointer rounded-lg border border-gray-200 bg-white px-6 py-3 transition-all duration-200 hover:shadow-md"
          >
            <h2 className="mb-1 text-center text-xs font-semibold text-gray-500 antialiased">Today</h2>
            <p className="text-center text-xl font-bold text-gray-600">{customerAtomList?.customer_count_info?.[0]?.Today || 0}</p>
          </div>

          <div
            onClick={() => {
              const today = moment().format('YYYY-MM-DD');
              const date7DaysAgo = moment().subtract(7, 'days').format('YYYY-MM-DD');
              setValue('fr_date', date7DaysAgo);
              setValue('to_date', today);
              searchByDate();
            }}
            className="cursor-pointer rounded-xl border border-gray-200 bg-white px-6 py-3 transition-all duration-200 hover:shadow-md"
          >
            <h2 className="mb-1 text-center text-xs font-semibold text-gray-500 antialiased">Last 7 Days</h2>
            <p className="text-center text-xl font-bold text-gray-600">{customerAtomList?.customer_count_info?.[0]?.Last_7_days || 0}</p>
          </div>

          <div
            onClick={() => {
              const firstDayOfMonth = moment().startOf('month').format('YYYY-MM-DD');
              const lastDayOfMonth = moment().endOf('month').format('YYYY-MM-DD');
              setValue('fr_date', firstDayOfMonth);
              setValue('to_date', lastDayOfMonth);
              searchByDate();
            }}
            className="cursor-pointer rounded-xl border border-gray-200 bg-white px-6 py-3 transition-all duration-200 hover:shadow-md"
          >
            <h2 className="mb-1 text-center text-xs font-semibold text-gray-500 antialiased">This Month</h2>
            <p className="text-center text-xl font-bold text-gray-600">{customerAtomList?.customer_count_info?.[0]?.Last_30_days || 0}</p>
          </div>

          <div
            onClick={() => {
              const firstDayOfYear = moment().startOf('year').format('YYYY-MM-DD');
              const date7DaysAgo = moment().subtract(7, 'days').format('YYYY-MM-DD');
              setValue('fr_date', firstDayOfYear);
              setValue('to_date', date7DaysAgo);
              searchByDate();
            }}
            className="cursor-pointer rounded-xl border border-gray-200 bg-white px-6 py-3 transition-all duration-200 hover:shadow-md"
          >
            <h2 className="mb-1 text-center text-xs font-semibold text-gray-500 antialiased">This Year</h2>
            <p className="text-center text-xl font-bold text-gray-600">{customerAtomList?.customer_count_info?.[0]?.Last_365_days || 0}</p>
          </div>

          <div
            onClick={() => {
              const firstDayOfLastYear = moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD');
              const lastDayOfLastYear = moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD');
              setValue('fr_date', firstDayOfLastYear);
              setValue('to_date', lastDayOfLastYear);
              searchByDate();
            }}
            className="cursor-pointer rounded-xl border border-gray-200 bg-white px-6 py-3 transition-all duration-200 hover:shadow-md"
          >
            <h2 className="mb-1 text-center text-xs font-semibold text-gray-500 antialiased">All</h2>
            <p className="text-center text-xl font-bold text-gray-600">{customerAtomList?.customer_count_info?.[0]?.All_Customer || 0}</p>
          </div>
        </div>

        <section className="mt-6 flex flex-col gap-6 rounded-2xl border border-gray-100 bg-white p-4 sm:p-5">
          {/* Top Actions: Toolbar */}
          <div className="flex flex-col gap-4 @xl:flex-row @xl:items-center @xl:justify-between">
            {/* Left side: Refresh & Filter */}
            <div className="flex w-auto flex-row items-center gap-3">
              <Tooltip title="Reload">
                <button
                  type="button"
                  onClick={() => loadCustomerListAction()}
                  className="flex items-center justify-center rounded-lg border border-gray-200 p-2 text-gray-500 transition-all hover:border-blue-200 hover:bg-gray-50 hover:text-blue-600 active:scale-95"
                >
                  <IoMdRefresh className="h-4 w-4" />
                </button>
              </Tooltip>

              <div className="flex-1 sm:min-w-[200px]">
                <CustomerPageFilter onFilterSelect={onFilterSelect} />
              </div>
            </div>

            {/* Right side: Actions */}
            <div className="flex w-auto items-center gap-3">
              {/* EXPORT BUTTON */}
              <button
                onClick={exportToCSV}
                className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2.5 font-[Poppins] text-sm font-semibold text-gray-700 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100 sm:flex-none sm:py-2"
              >
                <IoCloudDownloadOutline className="h-[18px] w-[18px] shrink-0 text-gray-500" />
                EXPORT CSV
              </button>

              <Link to={`/${currModuleId}/${currModuleName}/${currUserMenu}/add_customer`} className="flex-1 sm:flex-none">
                <button
                  title="Add New"
                  className="flex items-center justify-center rounded-lg border border-gray-200 p-2 text-gray-500 transition-all hover:border-blue-200 hover:bg-gray-50 hover:text-blue-600 active:scale-95"
                >
                  <IoAdd className="h-4 w-4" />
                </button>
              </Link>
            </div>
          </div>

          {/* Middle: Date search */}
          <div className="grid w-full gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-3 grid-auto-fit-sm sm:p-2.5">
            <div className="z-40 flex items-center gap-2">
              <span className="ml-1 min-w-[36px] text-xs font-semibold uppercase tracking-wider text-gray-500">From</span>
              <input
                defaultValue={moment(Date.now()).format('YYYY-MM-DD')}
                {...register('fr_date')}
                className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                type="date"
              />
            </div>

            <div className="z-40 flex items-center gap-2">
              <span className="ml-1 min-w-[36px] text-xs font-semibold uppercase tracking-wider text-gray-500">To</span>
              <input
                {...register('to_date')}
                defaultValue={moment(Date.now()).format('YYYY-MM-DD')}
                className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                type="date"
              />
            </div>

            <button
              onClick={searchByDate}
              type="button"
              className="w-full whitespace-nowrap rounded-lg bg-[#476EF8] px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-600 active:bg-blue-700 sm:w-auto"
            >
              Search
            </button>
          </div>
        </section>

        <section className="mt-5">
          {/* Bottom Row: Table Search & State Filters */}
          <div className="mb-4 flex flex-col justify-between gap-4 sm:items-center md:flex-row">
            {/* Table Search */}
            <div className="relative w-full sm:w-[350px]">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <IoSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search in table..."
                value={searchText}
                onChange={e => handleSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    onClearInput();
                  }
                }}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-10 text-sm text-gray-700 placeholder-gray-400 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
              />
              {searchText && (
                <button
                  onClick={onClearInput}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <IoClose className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* State Filters */}
            {customerSelect.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
                <span className="mr-1 whitespace-nowrap text-sm font-medium text-gray-500">{customerSelect.length} selected:</span>
                <button className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs font-semibold text-green-700 shadow-sm transition-colors hover:bg-green-100">
                  ACTIVE
                </button>
                <button className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 shadow-sm transition-colors hover:bg-amber-100">
                  IN ACTIVE
                </button>
                <button className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 shadow-sm transition-colors hover:bg-red-100">
                  DELETE
                </button>
              </div>
            )}
          </div>
          <GeneralCustomTable
            tableHeadList={columns}
            tableDataList={filteredCustomerList}
            allowCheckBox={true}
            setSelectedRows={selectCustomer}
            rowKey={record => record.Customer_Id}
            handleRowClick={handleRowClick}
          />
        </section>
      </section>
    </section>
  );
}

export default CustomersPage;
